export function inputToCents(value) {
  if (value === "" || value == null) return null;
  const amount = Number(value);
  return Number.isFinite(amount) ? Math.max(0, Math.round(amount * 100)) : null;
}

export function centsToInput(value) {
  if (value == null || value === "") return "";
  const amount = Number(value);
  return Number.isFinite(amount) ? String(amount / 100) : "";
}

// Statutory normal hours of a day (Labor Code Art. 83): the conversion between a daily and an hourly rate of a
// full-time agreement. A part-time agreement has no established normal day, so no conversion is shown.
const NORMAL_HOURS_PER_DAY = 8;
const SALARY_PERIODS_PER_YEAR = { monthly: 12, semi_monthly: 24, bi_weekly: 26, weekly: 52 };
// DOLE-BWC Handbook 2024 §D–E (server: modules/hr/shared/compensation-math.js).
const MONTHLY_PAID_DAYS = 365;
const DOLE_FACTORS = { DOLE_261: 261, DOLE_253: 253, DOLE_313: 313, DOLE_305: 305 };

export const isSalariedFrequency = (frequency) => Object.prototype.hasOwnProperty.call(SALARY_PERIODS_PER_YEAR, String(frequency || "").toLowerCase());

export function annualSalaryCents(baseSalaryCents, salaryFrequency) {
  const periods = SALARY_PERIODS_PER_YEAR[String(salaryFrequency || "").toLowerCase()];
  return periods ? Math.max(0, Math.round(Number(baseSalaryCents || 0))) * periods : null;
}

/** The least a stated daily rate of a salary can be: a salary cannot pay more days than a year has (annual ÷ 366). */
export function agreedDailyRateMinimum(baseSalaryCents, salaryFrequency) {
  const annual = annualSalaryCents(baseSalaryCents, salaryFrequency);
  return annual == null ? null : Math.ceil(annual / 366);
}

/** Days a year the salary pays at that daily rate (annual ÷ daily), one decimal. */
export function impliedPaidDays(baseSalaryCents, salaryFrequency, dailyRateCents) {
  const annual = annualSalaryCents(baseSalaryCents, salaryFrequency);
  return annual == null || !(Number(dailyRateCents) > 0) ? null : Math.round((annual / Number(dailyRateCents)) * 10) / 10;
}

/**
 * Preview of the daily and hourly rates the server will store (mirror of the server rule, Report 65): daily / hourly pay
 * is its own rate (converted over 8 normal hours when full-time); a salary that pays every day is annual ÷ 365; a salary
 * that pays the scheduled work days uses the basis its agreement documents — an agreed daily rate or the DOLE factor it
 * names. `undecidedReason`: COVERAGE_NOT_STATED | BASIS_NOT_STATED | DAILY_RATE_NOT_STATED | DAILY_RATE_OUT_OF_RANGE.
 * No business work week is read.
 */
export function deriveRatePreview(baseSalaryInput, salaryFrequency, { employmentType, salaryDayCoverage, conversionBasis, agreedDailyRateInput } = {}) {
  const baseSalaryCents = inputToCents(baseSalaryInput) || 0;
  const frequency = String(salaryFrequency || "monthly").toLowerCase();
  const workHoursPerDay = String(employmentType || "full_time").toLowerCase() === "part_time" ? null : NORMAL_HOURS_PER_DAY;
  const base = { workHoursPerDay, rateUndecided: false, undecidedReason: null, factor: null };
  const hourlyOf = (daily) => (workHoursPerDay ? Math.round(daily / workHoursPerDay) : null);
  const undecided = (undecidedReason) => ({ ...base, rateUndecided: true, undecidedReason, dailyRateCents: null, hourlyRateCents: null });
  if (frequency === "hourly") {
    return { ...base, hourlyRateCents: baseSalaryCents, dailyRateCents: workHoursPerDay ? Math.round(baseSalaryCents * workHoursPerDay) : null };
  }
  if (frequency === "daily") return { ...base, dailyRateCents: baseSalaryCents, hourlyRateCents: hourlyOf(baseSalaryCents) };
  const annual = annualSalaryCents(baseSalaryCents, frequency);
  if (salaryDayCoverage === "EVERY_DAY") {
    const daily = Math.round(annual / MONTHLY_PAID_DAYS);
    return { ...base, factor: MONTHLY_PAID_DAYS, dailyRateCents: daily, hourlyRateCents: hourlyOf(daily) };
  }
  if (salaryDayCoverage !== "SCHEDULED_WORK_DAYS") return undecided("COVERAGE_NOT_STATED");
  if (DOLE_FACTORS[conversionBasis]) {
    const daily = Math.round(annual / DOLE_FACTORS[conversionBasis]);
    return { ...base, factor: DOLE_FACTORS[conversionBasis], dailyRateCents: daily, hourlyRateCents: hourlyOf(daily) };
  }
  if (conversionBasis !== "AGREED_DAILY_RATE") return undecided("BASIS_NOT_STATED");
  const agreedDaily = inputToCents(agreedDailyRateInput);
  if (!(agreedDaily > 0)) return undecided("DAILY_RATE_NOT_STATED");
  // The server refuses it (salary-terms.js); the preview never shows a refused rate as the rate.
  if (agreedDaily < agreedDailyRateMinimum(baseSalaryCents, frequency)) return undecided("DAILY_RATE_OUT_OF_RANGE");
  return { ...base, dailyRateCents: agreedDaily, hourlyRateCents: hourlyOf(agreedDaily) };
}

/**
 * What a salary pays for, in the words the forms use. Policy A (decisions register 05 §4g): a new salary pays the
 * scheduled work days and paid holidays; "every day of the month" is kept only on agreements recorded before it.
 */
export const SALARY_COVERAGE_OPTIONS = [
  { value: "SCHEDULED_WORK_DAYS", label: "The scheduled work days and paid holidays" },
  { value: "EVERY_DAY", label: "Every day of the month, rest days included (older agreements)" },
];
export const NEW_SALARY_COVERAGE = "SCHEDULED_WORK_DAYS";
export const NEW_SALARY_COVERAGE_TEXT = "The scheduled work days and paid holidays. Rest days are not paid by the salary.";

const SALARY_PERIOD_WORD = { monthly: "month", semi_monthly: "half-month", bi_weekly: "two weeks", weekly: "week" };

/** Report 66 D3: HR says whether the amount is a fixed salary or an estimate of a daily / hourly wage. */
export function fixedSalaryQuestion(salaryFrequency) {
  const period = SALARY_PERIOD_WORD[String(salaryFrequency || "").toLowerCase()] || "pay period";
  return `Is this a fixed salary — the same agreed amount for every full ${period}?`;
}
export const FIXED_SALARY_HINT = "Yes: the agreed basic salary before deductions, contributions and tax, whatever the number of work days. It is not a promise of the same take-home pay. No: it is an estimate of what a daily or hourly wage earns.";
export const FIXED_SALARY_NO_TEXT = "Then it is not a fixed salary. Record daily or hourly pay at the agreed rate; a monthly estimate is not turned into a rate.";

/** APP-HR-060 (Apparify payroll policy): how unpaid days of a salary are valued, beside the daily rate's own use. */
export const UNPAID_DAY_TEXT = "An unpaid absence takes the pay period's salary ÷ the work days on the employee's schedule in that period — for example ₱10,000 over 11 work days is ₱909.09 a day. The daily rate above prices overtime and holiday pay.";
export const MINIMUM_WAGE_REMINDER = "Apparify does not check pay against the minimum wage. Make sure the pay meets the wage order that applies to this workplace.";

/** How a scheduled-days salary converts to a daily rate — the agreement's own term, never picked from the weekdays. */
export const CONVERSION_BASIS_OPTIONS = [
  { value: "AGREED_DAILY_RATE", label: "An agreed daily rate" },
  { value: "DOLE_261", label: "DOLE 261 · 5-day, specials paid" },
  { value: "DOLE_253", label: "DOLE 253 · 5-day, specials unpaid" },
  { value: "DOLE_313", label: "DOLE 313 · 6-day, specials paid" },
  { value: "DOLE_305", label: "DOLE 305 · 6-day, specials unpaid" },
];

/** Why a salary's daily / hourly rate is not shown yet, for HR. */
export const RATE_UNDECIDED_TEXT = {
  COVERAGE_NOT_STATED: "Not set: say what the salary pays for.",
  BASIS_NOT_STATED: "Not set: say how the agreement converts the salary to a daily rate.",
  DAILY_RATE_NOT_STATED: "Not set: enter the agreed daily rate.",
  DAILY_RATE_OUT_OF_RANGE: "Not set: that daily rate would mean the salary pays more days than a year has.",
};
